/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react';
import type { Locale } from '../types';
import { terminologyDictionary } from '../i18n/terminology';
type Dictionary = Record<string, string>;
const en: Dictionary = {
  'nav.home': 'Home',
  'nav.calculators': 'Calculators',
  'nav.calculatorsShort': 'Calculators',
  'nav.assessment': 'Solar Assessment',
  'nav.assessmentShort': 'Assessment',
  'nav.solarPlan': 'My Solar Plan',
  'nav.solarPlanShort': 'My Plan',
  'nav.decisionCenter': 'Solar Decision Center',
  'nav.decisionCenterShort': 'Decision Center',
  'nav.quoteChecker': 'Solar Quote Checker',
  'nav.dataExplorer': 'Philippine Solar Data',
  'nav.learn': 'Learn',
  'nav.learnShort': 'Learn',
  'nav.guides': 'Solar Guides',
  'nav.caseStudies': 'Case Studies',
  'nav.installers': 'Find Installers',
  'nav.installersShort': 'Installers',
  'nav.checklist': 'Installer Checklist',
  'nav.checklistShort': 'Checklist',
  'nav.methodology': 'Methodology',
  'nav.about': 'About',
  'nav.start': 'Start assessment',
  'nav.startShort': 'Get Started',
  'nav.more': 'More',
  'nav.openMenu': 'Open menu',
  'nav.closeMenu': 'Close menu',
  'nav.skip': 'Skip to main content',
  'nav.primary': 'Primary navigation',
  'nav.mobile': 'Mobile navigation',
  'nav.siteMenu': 'Site menu',
  'brand.tagline': 'Your guide to solar and electricity.',
  'footer.kicker': 'Plan before you spend',
  'footer.title': 'Turn your electricity questions into a clearer solar plan.',
  'footer.description':
    'Independent Philippine solar planning tools, learning resources, and browser-based installer discovery designed to help you ask better questions before contacting providers.',
  'footer.motto': 'Calculate. Compare. Decide.',
  'footer.plan': 'Plan',
  'footer.learn': 'Learn',
  'footer.company': 'Company',
  'footer.solarCalculators': 'Solar calculators',
  'footer.electricityCalculators': 'Electricity calculators',
  'footer.guides': 'Solar guides',
  'footer.sources': 'Sources',
  'footer.corrections': 'Corrections',
  'footer.contact': 'Contact',
  'footer.privacy': 'Privacy',
  'footer.terms': 'Terms',
  'footer.advertise': 'Advertise',
  'footer.disclaimer': 'Planning estimates only. Verify before spending.',
  'action.findInstallers': 'Find installers',
  'action.openCalculators': 'Open calculators',
  'action.startAssessment': 'Start solar assessment',
  'action.continue': 'Continue',
  'action.reviewMethodology': 'Review methodology',
  compareHeading: 'Compare installer quotations',
  addQuote: 'Add another quote',
  'home.seoTitle': 'Smarter Solar Decisions for Philippine Homes | SolarGabay PH',
  'home.seoDescription':
    'Use independent Philippine solar and electricity calculators, prepare installer questions, and find solar installers near you.',
  'home.heroTitle': 'Your solar journey starts here.',
  'home.heroDescription':
    'Reliable tools, local data, and practical guidance for planning solar in the Philippines.',
  'home.heroCalculator': 'Calculate Savings',
  'home.heroInstallers': 'Find Installers',
  'home.heroSearchTitle': 'Find solar installers near you',
  'home.heroSearchBody': 'Start with a place, then review local directory records.',
  'home.freeUse': '100% free to use',
  'home.localData': 'Local data and estimates',
  'home.builtForPhilippineHomes': 'Built for Philippine homes',
  'home.noSignup': 'No signup required',
  'home.previewCapacity': 'Capacity',
  'home.previewPanels': 'Panels',
  'home.previewSystemType': 'System type',
  'home.previewRoofSpace': 'Roof space',
  'home.benefitBillsTitle': 'Lower electricity bills',
  'home.benefitBillsBody': 'Use less grid electricity and understand where savings may come from.',
  'home.benefitValueTitle': 'Increase property value',
  'home.benefitValueBody': 'Plan a solar-ready home improvement with better information.',
  'home.benefitCleanTitle': 'Clean and sustainable',
  'home.benefitCleanBody': 'Reduce reliance on fossil-fuel electricity over time.',
  'home.benefitIndependenceTitle': 'More resilient energy supply',
  'home.benefitIndependenceBody': 'Prepare for rate increases and power interruptions.',
  'home.calculatorsTitle': 'Start with the right solar or electricity calculator.',
  'home.calculatorsDescription':
    'Use focused planning tools for bills, appliances, air-conditioning, solar savings, system size, payback, batteries, and roof fit.',
  'home.solarToolsTitle': 'Solar calculators',
  'home.solarToolsBody':
    'Estimate savings, system capacity, payback, battery needs, panels, and roof area.',
  'home.electricityToolsTitle': 'Electricity calculators',
  'home.electricityToolsBody':
    'Understand your bill, appliance consumption, and air-conditioner running cost.',
  'home.journeyTitle': 'A clear path from curious to ready',
  'home.journeyDescription':
    'SolarGabay PH connects calculation, education, preparation, and installer discovery into one practical customer journey.',
  'home.journeyCalculateTitle': 'Calculate your starting point',
  'home.journeyCalculateBody': 'Use solar and electricity calculators with visible assumptions.',
  'home.journeyLearnTitle': 'Learn the essentials',
  'home.journeyLearnBody':
    'Understand system types, batteries, roof considerations, and the questions that matter.',
  'home.journeyPrepareTitle': 'Prepare for installer calls',
  'home.journeyPrepareBody': 'Bring a clear checklist so quotations are easier to compare.',
  'home.journeyDiscoverTitle': 'Discover installers',
  'home.journeyDiscoverBody':
    'Search the country-aware global public-source installer directory and verify each provider directly.',
  'home.assessmentLabel': 'Solar assessment',
  'home.assessmentTitle': 'Get a practical starting point before anyone sells you a system.',
  'home.assessmentBody':
    'The guided assessment combines electricity use, daytime consumption, roof space, shading, grid conditions, backup needs, and future demand into an educational recommendation.',
  'home.previewLabel': 'Illustrative result preview',
  'home.previewTitle': 'Your solar starting point',
  'home.installerTitle': 'Search by installer, place, or solar service.',
  'home.installerBody':
    'Open the global directory with your country selected automatically when browser settings allow, then verify every company directly.',
  'home.city': 'City or municipality',
  'home.province': 'Province',
  'home.searchCompanies': 'Search Solar Installers',
  'home.checklistTitle': 'Know what to ask before comparing quotes.',
  'home.checklistBody':
    'Equipment models, warranties, installation scope, backup loads, monitoring, permits, and after-sales service can matter as much as headline capacity.',
  'home.openChecklist': 'Open installer checklist',
  'home.finalTitle': 'Start with clarity, not a sales pitch.',
  'home.finalBody':
    'Use the calculators, complete the assessment, save your checklist locally, and contact installers with a clearer project summary.',
  'directory.title': 'Solar and electricity calculators',
  'directory.description':
    'Choose a focused Philippine planning tool with transparent assumptions, ranges, and limitations.',
  'directory.solarTitle': 'Solar decision tools',
  'directory.solarBody':
    'Estimate savings, capacity, payback, battery size, panel count, and roof fit.',
  'directory.electricityTitle': 'Electricity cost tools',
  'directory.electricityBody':
    'Understand bills, appliance use, and air-conditioner costs before sizing solar.',
  'directory.browseSolar': 'Browse solar calculators',
  'directory.browseElectricity': 'Browse electricity calculators',
  'directory.search': 'Search calculators',
  'directory.searchPlaceholder': 'Search by bill, battery, roof, payback, aircon, or another goal',
  'directory.coreLabel': 'Planning tools',
  'directory.coreTitle': 'Choose a practical calculator',
  'directory.reviewed': 'Reviewed',
  'directory.open': 'Open calculator',
  'directory.standardTitle': 'Solar tool publishing standards',
  'directory.standardBody':
    'SolarGabay now keeps only calculators that directly support electricity understanding and solar decisions. Unrelated business, farming, and construction tools have been removed.',
  'calculator.reviewedBy': 'Reviewed by',
  'calculator.reviewed': 'Reviewed calculator',
  'calculator.updated': 'Updated',
  'calculator.formulaShown': 'Formula version appears with every result.',
  'calculator.confidence': 'Data confidence',
  'calculator.evidence': 'Evidence used',
  'calculator.variability': 'What can vary',
  'calculator.inputs': 'Your inputs',
  'calculator.inputsHelp': 'Use realistic values. Your entries remain on this device.',
  'calculator.simpleMode': 'Simple mode',
  'calculator.advancedMode': 'Advanced mode',
  'calculator.validationTitle': 'Please check the highlighted fields',
  'calculator.validationBody': 'Invalid values cannot be calculated silently.',
  'calculator.calculate': 'Calculate',
  'calculator.reset': 'Reset calculator',
  'calculator.results': 'Your estimate',
  'calculator.noResult': 'Enter your details and calculate to see the result.',
  'calculator.save': 'Save on this device',
  'calculator.share': 'Share result',
  'calculator.print': 'Print / Save as PDF',
  'calculator.saved': 'Saved on this device.',
  'calculator.range': 'Estimate range',
  'calculator.resultChart': 'Comparison chart for calculator results',
  'calculator.assumptions': 'Assumptions and formula details',
  'calculator.formulaVersion': 'Formula version',
  'calculator.related': 'Related calculators',
  'calculator.howTitle': 'How this estimate works',
  'calculator.howBody1':
    'Calculations are versioned, reviewed, and reproducible. Inputs are validated and planning ranges are used instead of false precision.',
  'calculator.howBody2':
    'Results support early decisions. Confirm utility rates, equipment specifications, site conditions, and professional requirements before committing money.',
  'calculator.findCompanies': 'Find solar installers for this project',
  'calculator.projectBrief': 'Create installer-ready project brief',
  'calculator.adjustInputs': 'Adjust calculator inputs',
  'loading.page': 'Preparing SolarGabay…',
  'loading.route': 'Opening this SolarGabay page…',
  'loading.section': 'Preparing this section…',
  'loading.results': 'Preparing your results…',
  'loading.wait': 'This should only take a moment. You can retry if it does not finish.',
  'loading.retry': 'Try again',
  'loading.timeout': 'This page is taking longer than expected.',
  'loading.timeoutBody':
    'Reload the page to retry. Your locally saved work will remain on this device.',
  'loading.error': 'This content could not be loaded.',
  'loading.installers': 'Finding installers that serve your area…',
  'loading.assessment': 'Preparing your solar assessment…',
  'loading.recommendation': 'Calculating your recommendation…',
  'loading.calculator': 'Preparing the calculator…',
  'loading.calculation': 'Calculating your estimate…',
  'loading.chart': 'Preparing the result chart…',
  'loading.guides': 'Preparing the solar guides…',
  'loading.country': 'Preparing your country and location options…',
  'common.home': 'Home',
  'common.pageNotFound': 'Page not found',
  'common.pageNotFoundBody': 'The requested route is unavailable.',
  'common.loading': 'Loading page',
  'common.reload': 'Reload page',
  'common.returnHome': 'Return home',
  'common.contactSupport': 'Contact support',
  'common.routeErrorTitle': 'This page could not load',
  'common.routeErrorBody':
    'Something unexpected happened. Your locally saved calculator and checklist data were not included in this support reference.',
  'common.supportReference': 'Support reference',
};
type LocaleContextValue = {
  locale: Locale;
  initialized: boolean;
  t: (key: string) => string;
};
const LocaleContext = createContext<LocaleContextValue | null>(null);
const dictionary: Dictionary = { ...en, ...terminologyDictionary.en };
export function LocaleProvider({ children }: { children: ReactNode }) {
  const locale: Locale = 'en';
  useEffect(() => {
    document.documentElement.lang = 'en-PH';
    document.documentElement.dataset.bootstrapLocale = 'en';
    window.localStorage.removeItem('solargabay.locale.v1');
  }, []);
  const value = useMemo<LocaleContextValue>(() => {
    return {
      locale,
      initialized: true,
      t: (key) => dictionary[key] || en[key] || key,
    };
  }, []);
  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}
export function useLocale() {
  const value = useContext(LocaleContext);
  if (!value) throw new Error('useLocale must be used within LocaleProvider');
  return value;
}
